/**
 * Onboarding Agreements — IN-2.c
 * ──────────────────────────────
 * Wizard-side writer that records an agreement signature against an
 * onboarding session. Reuses the IN-1.c invariants via
 * `recordAgreementSignature()` on a synthetic contractor wrapper, so
 * the variant-vs-resolver guard is shared.
 *
 * Hard rules:
 *   • `variant` must match `getRequiredCompliance(session).agreementVariant`
 *     (enforced by `recordAgreementSignature`).
 *   • `version` mandatory.
 *   • `typedName` mandatory; must match (case-insensitive trim) the
 *     session's draft.contactName.
 *   • Append-only — never overwrites prior signatures on the session.
 *   • No PDF generation, no e-sign integration, no legal storage.
 */

import { recordAgreementSignature } from "@/data/contractor/compliance/agreementSignatures";
import { getRequiredCompliance } from "@/data/contractor/compliance/complianceRequirements";
import type { Contractor } from "@/data/types";
import type { OnboardingAgreementSignature, OnboardingSession } from "./types";

export interface SignOnboardingAgreementInput {
  variant: NonNullable<Contractor["agreementSignatures"]>[number]["variant"];
  version: string;
  typedName: string;
  ipAddress?: string;
}

export function signOnboardingAgreement(
  session: OnboardingSession,
  input: SignOnboardingAgreementInput,
  actor: string,
): OnboardingSession {
  if (!session) {
    throw new Error("onboardingAgreements: session is required.");
  }
  const typed = (input?.typedName ?? "").trim();
  if (!typed) {
    throw new Error("onboardingAgreements: typed name is required.");
  }
  const expected = (session.draft.contactName ?? "").trim();
  if (expected && typed.toLowerCase() !== expected.toLowerCase()) {
    throw new Error(
      "onboardingAgreements: typed name does not match the contact name on file.",
    );
  }

  // Build a synthetic Contractor wrapper so we reuse the IN-1.c
  // variant-vs-resolver guard without touching any real contractor row.
  const synthetic = {
    id: session.id,
    entityType: session.entityType,
    parentEntityId: session.parentEntityId ?? null,
    agreementSignatures: (session.signatures ?? []).map((s) => ({
      id: s.id,
      variant: s.variant,
      version: s.version,
      signedAt: s.signedAt,
      signedBy: s.signedBy,
      ipAddress: s.ipAddress,
    })),
  } as unknown as Contractor;

  const { signature } = recordAgreementSignature(
    synthetic,
    {
      variant: input.variant,
      version: input.version,
      ipAddress: input.ipAddress,
    },
    actor,
  );

  return {
    ...session,
    signatures: [
      ...(session.signatures ?? []),
      { ...signature, typedName: typed },
    ],
    lastTouchedAt: new Date().toISOString(),
  };
}

function getAgreementSignatures(
  session: Pick<OnboardingSession, "signatures" | "draft">,
): OnboardingAgreementSignature[] {
  const columnSignatures = session.signatures ?? [];
  const draftSignatures = Array.isArray(session.draft?.signatures)
    ? session.draft.signatures
    : [];

  return [...columnSignatures, ...draftSignatures];
}

/** True if the session has a signature whose variant matches the
 *  required variant for its entity type. */
export function hasRequiredAgreementSignature(
  session: Pick<OnboardingSession, "entityType" | "parentEntityId" | "signatures" | "draft">,
): boolean {
  const reqs = getRequiredCompliance({
    entityType: session.entityType,
    parentEntityId: session.parentEntityId ?? null,
  });
  return getAgreementSignatures(session).some(
    (s) => s.variant === reqs.agreementVariant,
  );
}
